(() => {
  const SECTION_ID = 'blog-product-card';

  const LEGACY_SELECTOR =
    'div.blog-product-card[data-product-handle], div.blog-product-card[data-handle]';

  const BIS_MODAL_ID = '#ProductBISModal';

  const loadedScripts = new Set();

  function getHandle(el) {
    if (!el) {
      return '';
    }

    return (
      el.getAttribute('data-product-handle') ||
      el.getAttribute('data-handle') ||
      ''
    ).trim();
  }

  function loadScript(src) {
    if (!src || loadedScripts.has(src)) {
      return Promise.resolve();
    }

    const existing = document.querySelector(
      `script[src="${src}"]`
    );

    if (existing) {
      loadedScripts.add(src);
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.type = 'module';
      script.src = src;

      script.onload = () => {
        loadedScripts.add(src);
        resolve();
      };

      script.onerror = reject;

      document.body.appendChild(script);
    });
  }

  async function ensureComponents() {
    const map = window.components || {};

    const needed = [
      'modal-dialog',
      'modal-opener',
      'backinstock-block'
    ];

    await Promise.all(
      needed.map((name) => {
        if (map[name]) {
          return loadScript(map[name]);
        }

        return Promise.resolve();
      })
    );
  }

  async function fetchCardHtml(handle) {
    const root =
      (window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root) ||
      '/';

    const url =
      `${root}products/${encodeURIComponent(handle)}` +
      `?section_id=${SECTION_ID}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(
        `Unable to load product: ${handle}`
      );
    }

    const html = await response.text();

    const doc = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const section =
      doc.querySelector(
        `#shopify-section-${SECTION_ID}`
      ) || doc.body;

    const card = section.querySelector(
      '[data-blog-product-card]'
    );

    if (!card) {
      throw new Error(
        `Product card markup missing for: ${handle}`
      );
    }

    return card;
  }

  function getSelectedOptions(card) {
    const options = [];

    card
      .querySelectorAll(
        '.blog-product-card__option'
      )
      .forEach((optionEl) => {
        const input =
          optionEl.querySelector(
            '[data-product-option]:checked'
          );

        if (!input) {
          return;
        }

        /*
          custom.colors product swatches are NOT
          variant options.

          They only change the product card/product handle.
        */
        if (
          input.dataset.groupProduct === 'true'
        ) {
          return;
        }

        options.push(input.value);
      });

    return options;
  }

  function findVariant(
    variants,
    selectedOptions
  ) {
    if (!Array.isArray(variants)) {
      return null;
    }

    return variants.find((variant) => {
      if (
        !Array.isArray(variant.options) ||
        variant.options.length !== selectedOptions.length
      ) {
        return false;
      }

      return variant.options.every(
        (optionValue, index) =>
          optionValue === selectedOptions[index]
      );
    }) || null;
  }

  function updateSizeAvailability(
    card,
    variants
  ) {
    const optionRoots =
      card.querySelectorAll(
        '.blog-product-card__option'
      );

    optionRoots.forEach(
      (optionRoot, optionIndex) => {
        const isColorOption =
          optionRoot.dataset.isColor === 'true';

        if (isColorOption) {
          return;
        }

        const sizeLabels =
          optionRoot.querySelectorAll(
            '.blog-product-card__size'
          );

        if (!sizeLabels.length) {
          return;
        }

        sizeLabels.forEach((sizeEl) => {
          const input =
            sizeEl.querySelector(
              '[data-product-option]'
            );

          if (!input) {
            return;
          }

          const selectedOptions =
            getSelectedOptions(card);

          selectedOptions[optionIndex] =
            input.value;

          const matchingVariant =
            findVariant(
              variants,
              selectedOptions
            );

          const isUnavailable =
            !matchingVariant ||
            !matchingVariant.available;

          sizeEl.classList.toggle(
            'is-unavailable',
            isUnavailable
          );

          sizeEl.dataset.available =
            isUnavailable
              ? 'false'
              : 'true';

          input.dataset.available =
            isUnavailable
              ? 'false'
              : 'true';
        });
      }
    );
  }

  function updateSelectedLabels(card) {
    card
      .querySelectorAll(
        '.blog-product-card__option'
      )
      .forEach((optionEl) => {
        const label =
          optionEl.querySelector(
            '[data-blog-selected-option]'
          );

        const textEl =
          optionEl.querySelector(
            '[data-blog-selected-text]'
          );

        const checked =
          optionEl.querySelector(
            '[data-product-option]:checked'
          );

        if (!label || !checked) {
          return;
        }

        const next =
          checked.getAttribute(
            'data-color-name'
          ) ||
          checked.value;

        if (textEl) {
          textEl.textContent = next;
        } else {
          label.textContent = next;
        }

        label.dataset.defaultColorName = next;
      });

    /*
      Only custom.colors product swatches
      are treated as active color swatches.
    */
    card
      .querySelectorAll(
        '.blog-product-card__swatch[data-group-product="true"]'
      )
      .forEach((el) => {
        const input =
          el.querySelector(
            '[data-product-option]'
          );

        if (input) {
          el.classList.toggle(
            'is-selected',
            input.checked
          );
        }
      });

    card
      .querySelectorAll(
        '.blog-product-card__size'
      )
      .forEach((el) => {
        const input =
          el.querySelector('input');

        el.classList.toggle(
          'is-selected',
          Boolean(
            input &&
            input.checked
          )
        );
      });
  }

  function setCardImage(
    card,
    src
  ) {
    const imageEl =
      card.querySelector(
        '[data-blog-product-image]'
      );

    if (!imageEl || !src) {
      return;
    }

    if (
      imageEl.getAttribute('src') === src
    ) {
      return;
    }

    imageEl.src = src;
  }

  function animateColorName(
    optionRoot,
    nextName
  ) {
    if (
      !optionRoot ||
      !nextName
    ) {
      return;
    }

    const label =
      optionRoot.querySelector(
        '[data-blog-selected-option]'
      );

    const textEl =
      optionRoot.querySelector(
        '[data-blog-selected-text]'
      );

    if (!label || !textEl) {
      return;
    }

    if (
      (textEl.textContent || '').trim() ===
      nextName.trim()
    ) {
      return;
    }

    if (
      label.dataset.animatingName ===
      nextName
    ) {
      return;
    }

    if (label._animTimer) {
      clearTimeout(
        label._animTimer
      );

      label._animTimer = null;
    }

    label.dataset.animatingName =
      nextName;

    label.classList.remove(
      'is-enter'
    );

    label.classList.add(
      'is-exit'
    );

    label._animTimer =
      window.setTimeout(() => {
        textEl.style.transition =
          'none';

        label.classList.remove(
          'is-exit'
        );

        label.classList.add(
          'is-enter'
        );

        textEl.textContent =
          nextName;

        void textEl.offsetWidth;

        textEl.style.transition =
          '';

        requestAnimationFrame(() => {
          label.classList.remove(
            'is-enter'
          );

          textEl.style.transform =
            '';

          textEl.style.opacity =
            '';

          delete label.dataset
            .animatingName;

          label._animTimer =
            null;
        });
      }, 180);
  }

  function getColorName(previewEl) {
    if (!previewEl) {
      return '';
    }

    return (
      previewEl.getAttribute(
        'data-color-name'
      ) ||
      previewEl.getAttribute(
        'aria-label'
      ) ||
      ''
    );
  }

  function updateCardState(
    card,
    variant,
    variants
  ) {
    if (!variant) {
      return;
    }

    const select =
      card.querySelector(
        '[data-blog-variant-select]'
      );

    let selectedOption = null;

    if (select) {
      select.value =
        String(variant.id);

      if (
        select.tagName === 'SELECT'
      ) {
        selectedOption =
          select.selectedOptions &&
          select.selectedOptions[0];
      }
    }

    const priceEl =
      card.querySelector(
        '[data-blog-product-price]'
      );

    if (
      priceEl &&
      selectedOption &&
      selectedOption.dataset.priceHtml
    ) {
      let html =
        selectedOption.dataset.priceHtml;

      if (
        selectedOption.dataset.compareHtml
      ) {
        html +=
          ` <s>${selectedOption.dataset.compareHtml}</s>`;
      }

      priceEl.innerHTML =
        html;
    }

    const imageFromOption =
      selectedOption &&
      selectedOption.dataset.image;

    const imageFromVariant =
      variant.featured_image &&
      (
        typeof variant.featured_image ===
        'string'
          ? variant.featured_image
          : variant.featured_image.src
      );

    const src =
      imageFromOption ||
      imageFromVariant;

    if (src) {
      setCardImage(
        card,
        src
      );

      card.dataset.defaultImage =
        src;
    }

    const atc =
      card.querySelector(
        '[data-blog-atc]'
      );

    const bis =
      card.querySelector(
        '[data-blog-bis-opener]'
      );

    const available =
      Boolean(
        variant.available
      );

    if (atc) {
      atc.disabled =
        !available;

      atc.textContent =
        available
          ? atc.dataset
              .labelAvailable ||
            'Add to bag'
          : atc.dataset
              .labelSoldOut ||
            'Sold out';
    }

    if (bis) {
      bis.hidden =
        available;
    }

    card.classList.toggle(
      'blog-product-card__inner--soldout',
      !available
    );

    updateSelectedLabels(
      card
    );

    updateSizeAvailability(
      card,
      variants
    );
  }

  function syncSharedBisModal(card) {
    const modal =
      document.querySelector(
        BIS_MODAL_ID
      );

    if (!modal) {
      return;
    }

    const titleEl =
      modal.querySelector(
        '[data-modal-title]'
      );

    if (titleEl) {
      titleEl.textContent =
        card.dataset.productTitle ||
        '';
    }

    let variants = [];

    const variantsScript =
      card.querySelector(
        '[data-blog-variants]'
      );

    if (variantsScript) {
      try {
        variants =
          JSON.parse(
            variantsScript.textContent ||
            '[]'
          );
      } catch (error) {
        variants = [];
      }
    }

    const select =
      card.querySelector(
        '[data-blog-variant-select]'
      );

    const currentId =
      select
        ? String(select.value)
        : '';

    const unavailable =
      variants.filter(
        (variant) =>
          !variant.available
      );

    const variantInput =
      modal.querySelector(
        '[data-bis-variant-input]'
      );

    const variantWrap =
      modal.querySelector(
        '[data-bis-variant-wrap]'
      );

    if (!variantInput) {
      return;
    }

    if (
      variantInput.tagName ===
      'SELECT'
    ) {
      variantInput.innerHTML =
        '';

      const list =
        unavailable.length
          ? unavailable
          : variants;

      list.forEach(
        (variant) => {
          const option =
            document.createElement(
              'option'
            );

          option.value =
            String(variant.id);

          option.textContent =
            variant.title ||
            variant.name ||
            String(variant.id);

          if (
            String(variant.id) ===
            currentId
          ) {
            option.selected =
              true;
          }

          variantInput.appendChild(
            option
          );
        }
      );

      if (variantWrap) {
        variantWrap.hidden =
          list.length <= 1 &&
          unavailable.length === 0;
      }

      if (currentId) {
        variantInput.value =
          currentId;
      }
    } else {
      variantInput.value =
        currentId ||
        (
          unavailable[0] &&
          unavailable[0].id
        ) ||
        (
          variants[0] &&
          variants[0].id
        ) ||
        '';
    }
  }

  function bindCard(
    host,
    card
  ) {
    let variants = [];

    const variantsScript =
      card.querySelector(
        '[data-blog-variants]'
      );

    if (variantsScript) {
      try {
        variants =
          JSON.parse(
            variantsScript.textContent ||
            '[]'
          );
      } catch (error) {
        variants = [];
      }
    }

    const atc =
      card.querySelector(
        '[data-blog-atc]'
      );

    if (atc) {
      atc.dataset.labelAvailable =
        atc.dataset.labelAvailable ||
        atc.textContent.trim() ||
        'Add to bag';

      atc.dataset.labelSoldOut =
        atc.dataset.labelSoldOut ||
        'Sold out';
    }

    updateSizeAvailability(
      card,
      variants
    );

    updateSelectedLabels(
      card
    );

    card
      .querySelectorAll(
        '.blog-product-card__option'
      )
      .forEach((optionEl) => {
        const label =
          optionEl.querySelector(
            '[data-blog-selected-option]'
          );

        const textEl =
          optionEl.querySelector(
            '[data-blog-selected-text]'
          );

        if (label) {
          label.dataset.defaultColorName =
            (
              (textEl &&
                textEl.textContent) ||
              ''
            ).trim();
        }
      });

    card.addEventListener(
      'change',
      (event) => {
        const target =
          event.target;

        if (
          !target.matches(
            '[data-product-option]'
          )
        ) {
          return;
        }

        /*
          custom.colors product input is
          NOT a variant option.

          It only represents the currently
          loaded product/color.
        */
        if (
          target.dataset.groupProduct ===
          'true'
        ) {
          updateSelectedLabels(
            card
          );

          return;
        }

        const selected =
          getSelectedOptions(
            card
          );

        const variant =
          findVariant(
            variants,
            selected
          );

        updateSelectedLabels(
          card
        );

        updateSizeAvailability(
          card,
          variants
        );

        if (variant) {
          updateCardState(
            card,
            variant,
            variants
          );
        }
      }
    );

    if (
      !card.dataset.defaultImage
    ) {
      const imageEl =
        card.querySelector(
          '[data-blog-product-image]'
        );

      if (
        imageEl &&
        imageEl.getAttribute('src')
      ) {
        card.dataset.defaultImage =
          imageEl.getAttribute('src');
      }
    }

    let previewLock = 0;

    const swatches =
      card.querySelectorAll(
        '.blog-product-card__swatch[data-group-product="true"]'
      );

    const restoreDefaults =
      () => {
        const lock =
          previewLock;

        requestAnimationFrame(() => {
          if (
            lock !== previewLock
          ) {
            return;
          }

          if (
            card.querySelector(
              '.blog-product-card__swatch:hover'
            )
          ) {
            return;
          }

          setCardImage(
            card,
            card.dataset.defaultImage
          );

          const optionRoot =
            card.querySelector(
              '.blog-product-card__option[data-is-color="true"]'
            );

          const label =
            optionRoot &&
            optionRoot.querySelector(
              '[data-blog-selected-option]'
            );

          if (
            optionRoot &&
            label &&
            label.dataset
              .defaultColorName
          ) {
            animateColorName(
              optionRoot,
              label.dataset
                .defaultColorName
            );
          }
        });
      };

    swatches.forEach(
      (previewEl) => {
        previewEl.addEventListener(
          'mouseenter',
          () => {
            if (
              host &&
              (
                host._mounting ||
                host._previewPaused
              )
            ) {
              return;
            }

            previewLock += 1;

            const preview =
              previewEl.getAttribute(
                'data-preview-image'
              );

            if (preview) {
              setCardImage(
                card,
                preview
              );
            }

            const optionRoot =
              previewEl.closest(
                '.blog-product-card__option'
              );

            const colorName =
              getColorName(
                previewEl
              );

            if (
              optionRoot &&
              colorName
            ) {
              animateColorName(
                optionRoot,
                colorName
              );
            }
          }
        );

        previewEl.addEventListener(
          'mouseleave',
          () => {
            if (
              host &&
              (
                host._mounting ||
                host._previewPaused
              )
            ) {
              return;
            }

            restoreDefaults();
          }
        );
      }
    );

    card.addEventListener(
      'click',
      (event) => {
        const bisOpener =
          event.target.closest(
            '[data-blog-bis-opener]'
          );

        if (bisOpener) {
          syncSharedBisModal(
            card
          );

          return;
        }

        const swapBtn =
          event.target.closest(
            '[data-blog-product-swap]'
          );

        if (!swapBtn) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const nextHandle =
          swapBtn.getAttribute(
            'data-blog-product-swap'
          );

        if (
          !nextHandle ||
          !host
        ) {
          return;
        }

        if (
          getHandle(host) ===
          nextHandle
        ) {
          return;
        }

        /*
          Availability does NOT prevent switching.

          custom.colors products are valid color
          links even when unavailable.

          .is-unavailable is only visual/state.
        */
        host._previewPaused =
          true;

        host.setAttribute(
          'data-product-handle',
          nextHandle
        );

        host
          .mount({
            force: true
          })
          .finally(() => {
            host._previewPaused =
              false;
          });
      },
      true
    );
  }

  class BlogProductCard
    extends HTMLElement {

    connectedCallback() {
      if (
        !this.classList.contains(
          'blog-product-card'
        )
      ) {
        this.classList.add(
          'blog-product-card'
        );
      }

      this.mount();
    }

    async mount({
      force = false
    } = {}) {
      if (this._mounting) {
        return;
      }

      if (
        this.dataset
          .blogProductMounted ===
          'true' &&
        !force
      ) {
        return;
      }

      this._mounting =
        true;

      this.dataset.blogProductMounted =
        'true';

      const handle =
        getHandle(this);

      if (!handle) {
        this.innerHTML =
          '<div class="blog-product-card__error">Missing product handle.</div>';

        this._mounting =
          false;

        return;
      }

      this.classList.add(
        'is-loading'
      );

      this.setAttribute(
        'aria-busy',
        'true'
      );

      try {
        await ensureComponents();

        const card =
          await fetchCardHtml(
            handle
          );

        this.innerHTML =
          '';

        this.appendChild(
          card
        );

        bindCard(
          this,
          card
        );

        this.classList.remove(
          'is-loading'
        );

        this.removeAttribute(
          'aria-busy'
        );
      } catch (error) {
        console.error(
          error
        );

        this.classList.remove(
          'is-loading'
        );

        this.removeAttribute(
          'aria-busy'
        );

        this.innerHTML =
          '<div class="blog-product-card__error">Unable to load this product. Check the handle.</div>';
      } finally {
        this._mounting =
          false;

        this._previewPaused =
          false;
      }
    }
  }

  function upgradeLegacyPlaceholders(
    root = document
  ) {
    root
      .querySelectorAll(
        LEGACY_SELECTOR
      )
      .forEach((div) => {
        if (
          div.tagName.toLowerCase() ===
          'blog-product-card'
        ) {
          return;
        }

        if (
          div.closest(
            'blog-product-card'
          )
        ) {
          return;
        }

        const el =
          document.createElement(
            'blog-product-card'
          );

        Array.from(
          div.attributes
        ).forEach((attr) => {
          el.setAttribute(
            attr.name,
            attr.value
          );
        });

        if (
          !el.classList.contains(
            'blog-product-card'
          )
        ) {
          el.classList.add(
            'blog-product-card'
          );
        }

        div.replaceWith(
          el
        );
      });
  }

  if (
    !customElements.get(
      'blog-product-card'
    )
  ) {
    customElements.define(
      'blog-product-card',
      BlogProductCard
    );
  }

  function init() {
    upgradeLegacyPlaceholders();
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }
})();